import {FC, useState, CSSProperties, useEffect} from 'react'
import {KTCard} from '../../../_metronic/helpers'
import {TestManagement} from './TestManagement'
import {EditTestsModal} from './components/EditTestsModal'
import {ToastContainer} from 'react-toastify'
import TestHeader from './components/header/TestHeader'
import {
  collection,
  doc,
  onSnapshot,
  CollectionReference,
  Query,
  DocumentData,
  getDocs,
} from 'firebase/firestore'
import db from '../../../services/firebase'
import {useAuth} from '../../modules/auth'
import {useSearchParams} from 'react-router-dom'
import useAuditContext from 'app/Hooks/useAuditContext'
import {getReviewStatus} from './utils/reviewStatusUtils'

type Framework = {
  name: string
  checked: boolean
}

type TestDocument = {
  id: string
  framework?: string
  status?: string
  name?: string
  category?: string
  type?: string
  owner?: string
  [key: string]: any
}

const TestManagementWrapper: FC = () => {
  const categories = ['Policy', 'Human resources', 'Engineering', 'Risks', 'Information technology']
  const types = ['Document', 'Platform']
  const statusList = ['All', 'Passing', 'Failing', 'Deactivated']
  const auditStatusList = [
    'All',
    'Ready for audit',
    'Not ready for audit',
    'Approved',
    'Flagged',
    'Not applicable',
  ]

  const initialCheckedState = categories.map((category) => ({name: category, checked: false}))
  const initialCheckedTypes = types.map((type) => ({name: type, checked: false}))
  const [tests, setTests] = useState([] as any[])
  const [isLoading, setIsLoading] = useState(true)
  const [assetID, setAssetID] = useState<string>('')
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [checkedCategories, setCheckedCategories] = useState(initialCheckedState)
  const [checkedTypes, setCheckedTypes] = useState(initialCheckedTypes)
  const [checkedFrameworks, setCheckedFrameworks] = useState<Framework[]>([])
  const [status, setStatus] = useState('All')
  const [auditStatus, setAuditStatus] = useState('All')
  const [owner, setOwner] = useState('')
  const {currentUser} = useAuth()
  const [personnels, setPersonnels] = useState<any[]>([])
  const [sidebarTop, setSidebarTop] = useState(104)
  const [searchParams] = useSearchParams()
  const typeFromQuery = searchParams.get('type')
  const [selectedAudit, setSelectedAudit] = useState<any>(null)
  const [audits, setAudits] = useState<any[]>([])
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor, 
        }))
        .filter((audit) => audit.status !== 'Completed')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => 
              auditor.id === currentUser?.uid || auditor.email === currentUser?.email
            )
          } else {
            return audit.auditor.id === currentUser?.uid || audit.auditor.email === currentUser?.email
          }
        })
      }
      setAudits(Data)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (typeFromQuery && types.includes(typeFromQuery)) {
      setCheckedTypes(
        types.map((type) => ({
          name: type,
          checked: type === typeFromQuery,
        }))
      )
    }
  }, [typeFromQuery])

  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight
      const sidebarElement = document.getElementById('filterSidebar')
      const sidebarHeight = sidebarElement ? sidebarElement.offsetHeight : 0
      const newTop = windowHeight < sidebarHeight + 104 ? windowHeight - sidebarHeight - 20 : 104
      setSidebarTop(newTop)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const sidebarStyle: CSSProperties = {
    paddingLeft: '0px',
    width: '105%',
  }

  const mainContentStyle: CSSProperties = {
    marginLeft: '0%',
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) return
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const risksData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          end_date: doc.data().end_date,
        }))
        .filter((data) => !data.end_date)
      setPersonnels(risksData)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    getTests()
  }, [activeFrameworks])

  useEffect(() => {
    getActiveFramworks()
  }, [])

  useEffect(() => {
    if (audits && audits.length > 0) {
      const ongoingAudit = audits.find((audit) => audit.status === 'Ongoing')
      if (ongoingAudit) {
        setSelectedAudit(ongoingAudit)
      }
    }
  }, [audits])

  const getTests = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'tests')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const testsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().framework,
          status: doc.data().status,
          name: doc.data().name,
        }))

        setIsLoading(false)
        setTests(testsData)
      })

      return unsubscribe
    } catch (error) {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    filterTests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    auditStatus,
    owner,
    checkedCategories,
    checkedTypes,
    checkedFrameworks,
    selectedAudit,
  ])

  const filterTests = async () => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const testsCollectionRef = collection(tenantRef, 'tests')

    let q: CollectionReference<DocumentData> | Query<DocumentData> = testsCollectionRef

    try {
      const snapshot = await getDocs(q)
      let allDocs = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as TestDocument[]

      let filteredDocs = allDocs

      const applyFilters = (docs: TestDocument[]) => {
        let filtered = [...docs]

        if (currentUser?.role === 'Auditor' && selectedAudit?.framework) {
          const auditFrameworks = Array.isArray(selectedAudit.framework)
            ? selectedAudit.framework
            : selectedAudit.framework.split(',').map((fw: string) => fw.trim())

          filtered = filtered.filter((doc) => {
            const docFrameworks = (doc.framework || '').split(',').map((fw: string) => fw.trim())
            return auditFrameworks.some((fw: string) => docFrameworks.includes(fw))
          })
        }

        if (currentAuditContext) {
          filtered = filtered.filter((doc) => {
            const docFrameworks = (doc.framework || '').split(',').map((fw: string) => fw.trim())
            return currentAuditContext.framework.some((fw: string) => docFrameworks.includes(fw))
          })
        }

        if (status && status !== 'All') {
          filtered = filtered.filter((doc) => {
            if (status === 'Deactivated') {
              return doc.status === 'Deactivated'
            } else {
              return doc.status === (status === 'Failing' ? 'Fail' : 'Pass')
            }
          })
        }

        if (
          auditStatus &&
          auditStatus !== 'All' &&
          (currentUser?.role === 'Auditor' || currentAuditContext)
        ) {
          filtered = filtered.filter((doc) => {
            const currentAuditId = selectedAudit?.id || currentAuditContext?.id
            const review = getReviewStatus(doc.auditor_review, currentAuditId) || doc.status

            switch (auditStatus) {
              case 'Ready for audit':
                return review === 'Pass'
              case 'Not ready for audit':
                return review === 'Fail'
              case 'Approved':
                return review === 'Approved'
              case 'Flagged':
                return review === 'Flagged'
              case 'Not applicable':
                return review === 'Not applicable'
              default:
                return true
            }
          })
        }

        if (owner) {
          filtered = filtered.filter((doc) => doc.owner === owner)
        }

        const categoryNames = checkedCategories
          .filter((category) => category.checked)
          .map((category) => category.name)
        if (categoryNames.length > 0) {
          filtered = filtered.filter((doc) => categoryNames.includes(doc.category || ''))
        }

        const typeNames = checkedTypes.filter((type) => type.checked).map((type) => type.name)
        if (typeNames.length > 0) {
          filtered = filtered.filter((doc) => typeNames.includes(doc.type || ''))
        }

        if (currentUser?.role !== 'Auditor') {
          const frameworkNames = checkedFrameworks
            .filter((value) => value.checked)
            .map((value) => value.name.trim())

          const frameworksToFilter = frameworkNames.length > 0 
            ? frameworkNames 
            : activeFrameworks.map(fw => fw.trim())

          if (frameworksToFilter.length > 0 && !currentAuditContext) {
            filtered = filtered.filter((doc) => {
              const frameworks = (doc.framework || '').split(',').map((fw: string) => fw.trim())
              return frameworks.some((name) => frameworksToFilter.includes(name))
            })
          }
        }

        filtered.sort((a, b) => {
          const nameA = a.name || ''
          const nameB = b.name || ''
          return nameA.localeCompare(nameB)
        })

        return filtered
      }

      filteredDocs = applyFilters(allDocs)

      setIsLoading(false)
      setTests(filteredDocs)

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        let liveData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as TestDocument[]

        liveData = applyFilters(liveData)

        setIsLoading(false)
        setTests(liveData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error filtering tests:', error)
      setIsLoading(false)
      setTests([]) 
    }
  }

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
        setCheckedFrameworks(
          frameworks.map((framework) => ({
            name: framework,
            checked: activeFrameworks.includes(framework),
          }))
        )
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting tests:', error)
      setIsLoading(false)
    }
  }

  const onResetClicked = () => {
    setCheckedCategories(initialCheckedState)
    setCheckedTypes(initialCheckedTypes)
    setStatus('All')
    setAuditStatus('All')
    setOwner('')
    setCheckedFrameworks(activeFrameworks.map((framework) => ({name: framework, checked: false})))
    getTests()
  }

  const handleCategoryChange = (index: number) => {
    setCheckedCategories((prevState) => {
      const newState = [...prevState]
      newState[index].checked = !newState[index].checked
      return newState
    })
  }

  const handleFrameworkChange = (index: number) => {
    if (currentAuditContext) return

    setCheckedFrameworks((prevState) => {
      const newState = [...prevState]
      newState[index].checked = !newState[index].checked
      return newState
    })
  }

  const handleTypeChange = (index: number) => {
    setCheckedTypes((prevState) => {
      const newState = [...prevState]
      newState[index].checked = !newState[index].checked
      return newState
    })
  }

  return (
    <>
      <div style={{height: '100vh', display: 'flex', flexDirection: 'column'}}>
        {isLoading ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <div className='row'>
            {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
              <div className='col-md-2 d-none d-sm-block'>
                <div
                  id='filterSidebar'
                  style={{
                    ...sidebarStyle,
                    position: 'sticky',
                    top: `${sidebarTop}px`,
                    maxWidth: '105%',
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                    minHeight: '85vh',
                    maxHeight: '100vh',
                  }}
                >
                  <div className='card h-100' style={{background: '#fff'}}>
                    <div className='card-body p-0 pt-4'>
                      <div className='d-flex justify-content-between align-items-center mb-2 px-2'>
                        <label className='fs-6 form-label fw-bold text-gray-900 mb-0 ps-3'>
                          Filters
                        </label>
                        <button
                          className='btn btn-sm fs-6 text-bold btn-active-light-primary mx-0 px-2'
                          style={{color: '#0E9F6E'}}
                          onClick={() => onResetClicked()}
                        >
                          Reset
                        </button>
                      </div>
                      <div className='px-5'>
                        <div className='mb-4'>
                          <label className='fs-6 form-label fw-bold text-gray-900 mb-5'>
                            Framework
                          </label>
                          {currentAuditContext ? (
                            <div className='form-check form-check-custom mb-5 form-check-sm'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={true}
                                disabled={true}
                              />
                              <label className='form-check-label flex-grow-1 fw-semibold text-gray-700 fs-7'>
                                {currentAuditContext.framework[0]}
                              </label>
                            </div>
                          ) : currentUser?.role === 'Auditor' ? (
                            <div className='form-check form-check-custom mb-5 form-check-sm'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={true}
                                disabled={true}
                              />
                              <label className='form-check-label flex-grow-1 fw-semibold text-gray-700 fs-7'>
                                {audits.map((audit) => audit.framework).join(', ')}
                              </label>
                            </div>
                          ) : (
                            checkedFrameworks.map((framework, index) => (
                              <div
                                key={index}
                                className='form-check form-check-custom mb-5 form-check-sm'
                              >
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  id={`kt_search_framework_${index + 1}`}
                                  onChange={() => handleFrameworkChange(index)}
                                  checked={framework.checked}
                                />
                                <label
                                  className='form-check-label flex-grow-1 fw-semibold text-gray-700 fs-7'
                                  onClick={() => handleFrameworkChange(index)}
                                >
                                  {framework.name}
                                </label>
                              </div>
                            ))
                          )}
                        </div>

                        <div className='mb-4'>
                          <label className='fs-6 form-label fw-bold text-gray-900 mb-5'>Type</label>
                          {checkedTypes.map((type, index) => (
                            <div
                              key={index}
                              className='form-check form-check-custom mb-5 form-check-sm'
                            >
                              <input
                                className='form-check-input'
                                type='checkbox'
                                id={`kt_search_type_${index + 1}`}
                                onChange={() => handleTypeChange(index)}
                                checked={type.checked}
                              />
                              <label
                                className='form-check-label flex-grow-1 fw-semibold text-gray-700 fs-7'
                                onClick={() => handleTypeChange(index)}
                              >
                                {type.name}
                              </label>
                            </div>
                          ))}
                        </div>

                        <div className='mb-4'>
                          <label className='fs-6 form-label fw-bold text-gray-900 mb-5'>
                            Categories
                          </label>
                          {checkedCategories.map((category, index) => (
                            <div
                              key={index}
                              className='form-check form-check-custom mb-5 form-check-sm'
                            >
                              <input
                                className='form-check-input'
                                type='checkbox'
                                id={`kt_search_category_${index + 1}`}
                                onChange={() => handleCategoryChange(index)}
                                checked={category.checked}
                                disabled={
                                  checkedTypes.some(
                                    (type) => type.name === 'Platform' && type.checked
                                  ) &&
                                  !checkedTypes.some(
                                    (type) => type.name === 'Document' && type.checked
                                  )
                                }
                              />
                              <label
                                className='form-check-label flex-grow-1 fw-semibold text-gray-700 fs-7'
                                onClick={() => handleCategoryChange(index)}
                              >
                                {category.name}
                              </label>
                            </div>
                          ))}
                        </div>
                        {!currentAuditContext && currentUser?.role !== 'Auditor' && (
                          <div className='mb-4'>
                            <label className='fs-6 form-label fw-bold text-gray-900'>Status</label>
                            <select
                              className='form-select'
                              value={status}
                              onChange={(e) => setStatus(e.target.value)}
                            >
                              {statusList.map((statusOption, index) => (
                                <option key={index} value={statusOption}>
                                  {statusOption}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {(currentUser?.role === 'Auditor' || currentAuditContext) && (
                          <div className='mb-4'>
                            <label className='fs-6 form-label fw-bold text-gray-900'>Status</label>
                            <select
                              className='form-select'
                              value={auditStatus}
                              onChange={(e) => setAuditStatus(e.target.value)}
                            >
                              {auditStatusList.map((statusOption, index) => (
                                <option key={index} value={statusOption}>
                                  {statusOption}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {!currentAuditContext && currentUser?.role !== 'Auditor' && (
                          <div className='mb-6'>
                            <label className='fs-6 form-label fw-bold text-gray-900'>Owner</label>
                            <select
                              className='form-select'
                              value={owner}
                              onChange={(e) => setOwner(e.target.value)}
                            >
                              <option value='' selected>
                                Select Owner
                              </option>
                              {personnels
                                .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                                .map((group) => (
                                  <option key={group.id} value={group.full_name}>
                                    {group.full_name}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div
              className={`${currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? 'col-md-12' : 'col-md-10'}`}
              style={mainContentStyle}
            >
              <TestHeader />
              <KTCard>
                <TestManagement
                  className='mb-5 mb-xl-8'
                  tests={tests}
                  status={status}
                  audit={selectedAudit}
                />
                {assetID && <EditTestsModal id={assetID} setTestsID={setAssetID} />}
                <ToastContainer />
              </KTCard>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default TestManagementWrapper
